import React, { FC, ReactNode } from 'react'
import { SideSheet, Switch, Input, InputNumber, TextArea } from '@douyinfe/semi-ui'
import { IconTick, IconSun, IconMoon, IconDesktop } from '@douyinfe/semi-icons'
import { useIntl } from 'react-intl'
import useStore, { type Theme, type ContainerWidth, type SeoConfig } from '@src/store/common/global'
import { HUE_ORDER, HUE_SWATCH, type PrimaryHue } from '@src/theme/palette'

/** Sider 宽度上下限 — 与 store 中的 SIDER_WIDTH_MIN/MAX 保持一致 */
const SIDER_WIDTH_MIN = 160
const SIDER_WIDTH_MAX = 320

const SWATCH_SIZE = 32

const SectionTitle: FC<{ id: string }> = ({ id }) => {
	const { formatMessage: i18nMsg } = useIntl()
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: 12,
				margin: '20px 0 16px'
			}}
		>
			<div
				style={{
					flex: 1,
					height: 1,
					background: 'var(--semi-color-border)'
				}}
			/>
			<span
				style={{
					fontSize: 13,
					fontWeight: 500,
					color: 'var(--semi-color-text-2)',
					letterSpacing: 0.3
				}}
			>
				{i18nMsg({ id })}
			</span>
			<div
				style={{
					flex: 1,
					height: 1,
					background: 'var(--semi-color-border)'
				}}
			/>
		</div>
	)
}

/**
 * 3 选 1 选项卡（用于"主题风格" / "容器宽度"）：
 * - 选中：背景淡主色 + 文字主色 + 字重 600 + 外圈 2px 主色线
 * - 键盘可达（role=radio, tabIndex, Enter/Space）
 */
interface OptionCardProps<T extends string> {
	value: T
	selected: boolean
	icon: ReactNode
	label: string
	onSelect: (v: T) => void
}
function OptionCard<T extends string>({ value, selected, icon, label, onSelect }: OptionCardProps<T>) {
	return (
		<div
			role="radio"
			aria-checked={selected}
			tabIndex={0}
			onClick={() => onSelect(value)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					onSelect(value)
				}
			}}
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				gap: 8,
				padding: '14px 4px',
				borderRadius: 8,
				border: '1px solid var(--semi-color-border)',
				background: selected ? 'var(--semi-color-primary-light-default)' : 'var(--semi-color-bg-2)',
				boxShadow: selected ? '0 0 0 2px var(--semi-color-primary)' : '0 0 0 0 transparent',
				color: selected ? 'var(--semi-color-primary)' : 'var(--semi-color-text-1)',
				cursor: 'pointer',
				transition: 'background 0.2s, box-shadow 0.2s, color 0.2s',
				outline: 'none'
			}}
		>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
			<span
				style={{
					fontSize: 13,
					fontWeight: selected ? 600 : 400,
					whiteSpace: 'nowrap'
				}}
			>
				{label}
			</span>
		</div>
	)
}

/**
 * 宽度示意图标：固定 28×28 viewBox，左右两个箭头 ←  →
 * - 箭头本身的尺寸恒定，仅「两个箭头尾端的距离」随 value 变化
 *   紧凑=18 / 中等=14 / 舒展=10
 *   越紧凑 → 容器越宽 → 箭头分得越开；越舒展 → 内容越居中 → 箭头越靠拢
 * - 使用 stroke（1.5px）而非 fill，视觉重量更轻，三档粗细一致
 */
const WidthIcon: FC<{ value: ContainerWidth }> = ({ value }) => {
	const tailGap = value === 'compact' ? 18 : value === 'medium' ? 14 : 10
	const cy = 14
	const leftTail = 14 - tailGap / 2
	const rightTail = 14 + tailGap / 2
	const len = 4
	const headH = 2.5
	return (
		<svg width="28" height="28" viewBox="0 0 28 28" aria-hidden>
			<g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
				{/* 左箭头 ← */}
				<path
					d={`M ${leftTail} ${cy} L ${leftTail - len} ${cy} M ${leftTail - len + headH} ${cy - headH} L ${leftTail - len} ${cy} L ${leftTail - len + headH} ${cy + headH}`}
				/>
				{/* 右箭头 → */}
				<path
					d={`M ${rightTail} ${cy} L ${rightTail + len} ${cy} M ${rightTail + len - headH} ${cy - headH} L ${rightTail + len} ${cy} L ${rightTail + len - headH} ${cy + headH}`}
				/>
			</g>
		</svg>
	)
}

/**
 * 紧凑行 — 用于 Switch / InputNumber 这种本身已带尺寸的紧凑控件
 * - 横向单行：label 左 + 控件右
 * - label 14px 略大、字重 800
 * - 控件自然宽度（Switch 自适应、InputNumber 通过 children 自己设宽）
 * - 行高 32px 居中，与控件高度对齐
 */
const CompactRow: FC<{ label: ReactNode; children: ReactNode }> = ({ label, children }) => (
	<div
		style={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			gap: 12,
			minWidth: 0,
			height: 32
		}}
	>
		<span
			style={{
				fontSize: 14,
				fontWeight: 800,
				color: 'var(--semi-color-text-1)',
				lineHeight: '20px',
				flexShrink: 0,
				whiteSpace: 'nowrap'
			}}
		>
			{label}
		</span>
		<div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}>{children}</div>
	</div>
)

/**
 * 字段行 — 用于 Input / TextArea 这种需要全宽的长文本输入
 * - 垂直两行：label 在上 + 控件在下
 * - label 14px、字重 800
 * - 控件宽度撑满容器
 */
const FieldRow: FC<{ label: ReactNode; children: ReactNode }> = ({ label, children }) => (
	<div
		style={{
			display: 'flex',
			flexDirection: 'column',
			gap: 6,
			minWidth: 0
		}}
	>
		<span
			style={{
				fontSize: 14,
				fontWeight: 800,
				color: 'var(--semi-color-text-1)',
				lineHeight: '20px'
			}}
		>
			{label}
		</span>
		<div style={{ width: '100%', minWidth: 0 }}>{children}</div>
	</div>
)

const ThemeSettings: FC = () => {
	const { formatMessage: i18nMsg } = useIntl()
	const primaryHue = useStore((s) => s.primaryHue)
	const setPrimaryHue = useStore((s) => s.setPrimaryHue)
	const open = useStore((s) => s.themeDrawerOpen)
	const setOpen = useStore((s) => s.setThemeDrawerOpen)
	const theme = useStore((s) => s.theme)
	const setTheme = useStore((s) => s.setTheme)
	const containerWidth = useStore((s) => s.containerWidth)
	const setContainerWidth = useStore((s) => s.setContainerWidth)
	const watermarkEnabled = useStore((s) => s.watermarkEnabled)
	const setWatermarkEnabled = useStore((s) => s.setWatermarkEnabled)
	const systemName = useStore((s) => s.systemName)
	const setSystemName = useStore((s) => s.setSystemName)
	const copyright = useStore((s) => s.copyright)
	const setCopyright = useStore((s) => s.setCopyright)
	const seo = useStore((s) => s.seo)
	const setSeo = useStore((s) => s.setSeo)
	const siderWidth = useStore((s) => s.siderWidth)
	const setSiderWidth = useStore((s) => s.setSiderWidth)

	/** SEO 子字段的便捷更新：保留其它字段，只改一个 key */
	const updateSeo = (patch: Partial<SeoConfig>) => setSeo({ ...seo, ...patch })

	const themeOptions: { value: Theme; icon: ReactNode; label: string }[] = [
		{ value: 'light', icon: <IconSun size="large" />, label: i18nMsg({ id: 'app.theme.light' }) },
		{ value: 'dark', icon: <IconMoon size="large" />, label: i18nMsg({ id: 'app.theme.dark' }) },
		{ value: 'system', icon: <IconDesktop size="large" />, label: i18nMsg({ id: 'app.theme.style.system' }) }
	]
	const widthOptions: { value: ContainerWidth; label: string }[] = [
		{ value: 'compact', label: i18nMsg({ id: 'app.theme.width.compact' }) },
		{ value: 'medium', label: i18nMsg({ id: 'app.theme.width.medium' }) },
		{ value: 'spacious', label: i18nMsg({ id: 'app.theme.width.spacious' }) }
	]

	return (
		<SideSheet
			title={i18nMsg({ id: 'app.theme.title' })}
			visible={open}
			onCancel={() => setOpen(false)}
			closable
			maskClosable
			width={360}
			placement="right"
		>
			{/* 系统主题色 */}
			<SectionTitle id="app.theme.section.color" />
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(6, 1fr)',
					gap: 12
				}}
			>
				{HUE_ORDER.map((h) => {
					const selected = primaryHue === h
					return (
						<div
							key={h}
							role="button"
							tabIndex={0}
							aria-label={h}
							onClick={() => setPrimaryHue(h as PrimaryHue)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									setPrimaryHue(h as PrimaryHue)
								}
							}}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								cursor: 'pointer',
								outline: 'none'
							}}
						>
							<div
								style={{
									width: SWATCH_SIZE,
									height: SWATCH_SIZE,
									borderRadius: '50%',
									background: HUE_SWATCH[h],
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: '#fff',
									boxShadow: selected
										? `0 0 0 1.5px var(--semi-color-bg-2), 0 0 0 3.5px ${HUE_SWATCH[h]}`
										: '0 1px 2px rgba(0,0,0,0.12)',
									transition: 'transform 0.15s, box-shadow 0.2s',
									transform: selected ? 'scale(1.05)' : 'scale(1)'
								}}
							>
								{selected ? <IconTick size="small" /> : null}
							</div>
						</div>
					)
				})}
			</div>

			{/* 主题风格 */}
			<SectionTitle id="app.theme.section.style" />
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(3, 1fr)',
					gap: 10
				}}
			>
				{themeOptions.map((opt) => (
					<OptionCard
						key={opt.value}
						value={opt.value}
						selected={theme === opt.value}
						icon={opt.icon}
						label={opt.label}
						onSelect={setTheme}
					/>
				))}
			</div>

			{/* 容器宽度 */}
			<SectionTitle id="app.theme.section.width" />
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(3, 1fr)',
					gap: 10
				}}
			>
				{widthOptions.map((opt) => (
					<OptionCard
						key={opt.value}
						value={opt.value}
						selected={containerWidth === opt.value}
						icon={<WidthIcon value={opt.value} />}
						label={opt.label}
						onSelect={setContainerWidth}
					/>
				))}
			</div>

			{/* 全局配置 */}
			<SectionTitle id="app.theme.section.global" />
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 14
				}}
			>
				{/* 单行：左 全局水印 / 右 Switch — 横向，label 略大 */}
				<CompactRow label={i18nMsg({ id: 'app.theme.watermark' })}>
					<Switch checked={watermarkEnabled} onChange={(v) => setWatermarkEnabled(v)} />
				</CompactRow>
				{/* 单行：左 侧边栏宽度 / 右 InputNumber — 横向，label 略大 */}
				<CompactRow label={i18nMsg({ id: 'app.theme.siderWidth' })}>
					<InputNumber
						value={siderWidth}
						onChange={(v) => setSiderWidth(Number(v))}
						min={SIDER_WIDTH_MIN}
						max={SIDER_WIDTH_MAX}
						step={4}
						suffix="px"
						style={{ width: 120 }}
					/>
				</CompactRow>
				{/* 两行：label 上 / Input 下 — 长文本字段 */}
				<FieldRow label={i18nMsg({ id: 'app.theme.systemName' })}>
					<Input
						value={systemName}
						onChange={(v) => setSystemName(v)}
						placeholder="Plus-One UI"
						maxLength={32}
						showClear
					/>
				</FieldRow>
				<FieldRow label={i18nMsg({ id: 'app.theme.copyright' })}>
					<Input
						value={copyright}
						onChange={(v) => setCopyright(v)}
						placeholder="© 2024 Plus-One UI. All rights reserved."
						maxLength={120}
						showClear
					/>
				</FieldRow>
			</div>

			{/* SEO 配置 — 与「系统配置」平级的独立 Section */}
			<SectionTitle id="app.theme.section.seo" />
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					gap: 14
				}}
			>
				<FieldRow label={i18nMsg({ id: 'app.theme.seo.title' })}>
					<Input
						value={seo.title}
						onChange={(v) => updateSeo({ title: v })}
						placeholder="Plus-One UI"
						maxLength={60}
						showClear
					/>
				</FieldRow>
				<FieldRow label={i18nMsg({ id: 'app.theme.seo.keywords' })}>
					<Input
						value={seo.keywords}
						onChange={(v) => updateSeo({ keywords: v })}
						placeholder="keyword1, keyword2"
						maxLength={120}
						showClear
					/>
				</FieldRow>
				<FieldRow label={i18nMsg({ id: 'app.theme.seo.description' })}>
					<TextArea
						value={seo.description}
						onChange={(v) => updateSeo({ description: v })}
						placeholder="A short description of the system."
						rows={3}
						maxLength={200}
						showClear
					/>
				</FieldRow>
			</div>
		</SideSheet>
	)
}

export default ThemeSettings
